import { Listbox, Transition, ListboxOptions, ListboxButton, ListboxOption, Label } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/solid';
import classNames from 'classnames';
import { Children, MouseEventHandler, PropsWithChildren, ReactElement, memo, useCallback, useMemo } from 'react';
import { T } from '../../../translation/src';

interface OptionProps {
  value: unknown;
  children?: React.ReactNode;
}

const Option_: React.FC2<OptionProps> = ({ value, children }) => {
  return (
    <ListboxOption
      className={({ focus }) => `relative select-none py-2 pl-10 pr-4 cursor-pointer  ${focus ? 'group bg-neon-green-300 dark:bg-eerie-black dark:text-white' : 'bg-white text-gray-900 dark:bg-[#313131] dark:text-white'}`}
      value={value}
    >
      {({ selected }) => (
        <>
          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>{children}</span>
          {selected ? (
            <span className={classNames('text-neon-green-700 dark:text-neon-green-300', 'absolute inset-y-0 left-0 flex items-center pl-3')}>
              <CheckIcon className="w-5 h-5" aria-hidden="true" />
            </span>
          ) : null}
        </>
      )}
    </ListboxOption>
  );
};
export const Option = memo(Option_);

export const Divider: React.FC2 = () => {
  return (
    <Label>
      <div className="border-t-2 border-black dark:border-white" />
    </Label>
  );
};

export interface SelectProps<Value = string> {
  value: Value | undefined;
  name?: string;
  onChange: (event: { target: { value: Value; name: string } }) => void;
  placeholder?: React.ReactNode;
  isLabelPlaceholder?: boolean;
  className?: string;
  id?: string;
  multiple?: boolean;
  clearable?: boolean;
  error?: boolean;
  disabled?: boolean;
  mixed?: boolean;
  renderSelectedValue?: (value: Value) => React.ReactNode;
  /**
   * Render the options in a portal anchored to the button (flips up when there
   * is no room below) — for selects inside dialogs/panels that clip overflow.
   */
  anchored?: boolean;
  /** Center the selected value (panels whose number fields are centered: the properties panel). */
  centered?: boolean;
}
const Select_ = <Value = string,>({ value, name, onChange: propsOnChange, children, placeholder, isLabelPlaceholder, className, multiple, clearable, error, disabled, mixed, renderSelectedValue, anchored, centered }: PropsWithChildren<SelectProps<Value>>): ReactElement | null => {
  const onChange = useCallback(
    (selectedValue: Value) => {
      propsOnChange?.({ target: { value: selectedValue, name: name || '' } });
    },
    [name, propsOnChange],
  );

  const onClear: MouseEventHandler<HTMLSpanElement> = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      propsOnChange({ target: { value: (multiple ? [] : '') as Value, name: name || '' } });
    },
    [multiple, name, propsOnChange],
  );

  const showClear = useMemo(() => {
    if (!clearable) return false;
    if (!multiple) return !!value;
    return !!value && !!(value as unknown[]).length;
  }, [multiple, value, clearable]);

  const selectedValueLabel = useMemo(() => {
    if (multiple)
      return (
        <span>
          {(value as unknown[]).length} <T _str="items selected" />
        </span>
      );
    if (renderSelectedValue && value !== undefined) return renderSelectedValue(value);
    let foundLabel: React.ReactNode = '';
    Children.toArray(children).some((child) => {
      const props = (child as ReactElement<OptionProps>).props;
      if (props?.value !== value) return false;
      foundLabel = props.children;
      return true;
    });
    return foundLabel;
  }, [children, multiple, value, renderSelectedValue]);

  return (
    <div className={classNames(!className?.includes('w-') && 'w-full', 'text-sm h-10 cursor-pointer', className)}>
      <Listbox value={value} onChange={onChange} multiple={multiple} disabled={disabled}>
        <div className="relative h-full">
          <ListboxButton
            className={classNames(
              mixed
                ? 'bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 italic border border-gray-300 hover:border-gray-400 h-full'
                : 'bg-white border border-gray-300 hover:border-gray-400 dark:bg-[#313131] dark:text-white h-full',
              'relative w-full py-2 pr-8 border  rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 sm:text-sm',
              // Centered: room for the chevron on both sides, so the value sits in the true middle.
              centered ? 'pl-8 text-center' : 'pl-3 text-left',
              !!error && 'border-red-500',
            )}
          >
            {placeholder && isLabelPlaceholder && <span className="text-gray-400 truncate">{placeholder}: </span>}
            {placeholder && !isLabelPlaceholder && !selectedValueLabel && <span className="text-gray-400 truncate">{placeholder} </span>}
            <span className={classNames('truncate', isLabelPlaceholder ? 'font-bold ml-auto float-right' : 'block')}>{selectedValueLabel}</span>
            <span className={classNames('absolute inset-y-0 right-0 flex items-center pr-2', !showClear && 'pointer-events-none')} {...(showClear && { onClick: onClear })}>
              {showClear ? (
                <XMarkIcon className={classNames('text-gray-400 dark:text-neon-green-300', ' w-5 h-5')} aria-hidden="true" />
              ) : (
                <ChevronDownIcon className={classNames('text-neon-green-700 dark:text-neon-green-300', 'w-5 h-5')} aria-hidden="true" />
              )}
            </span>
          </ListboxButton>
          <Transition leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            <ListboxOptions
              anchor={anchored ? { to: 'bottom start', gap: 4 } : undefined}
              className={classNames(
                'bg-white ring-black dark:bg-[#313131] dark:ring-white',
                anchored ? 'z-[60] w-[var(--button-width)]' : 'absolute z-40 w-full mt-1',
                'py-1 overflow-auto text-base rounded-md shadow-lg max-h-60 ring-1 ring-opacity-5 focus:outline-none sm:text-sm',
              )}
            >
              {children}
            </ListboxOptions>
          </Transition>
        </div>
      </Listbox>
    </div>
  );
};
export const Select = memo(Select_) as typeof Select_;
